import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Upload, Trash2, Save, Plus, RefreshCw, Key, LayoutDashboard } from 'lucide-react';
import './CompanyBranding.css';

// Matches the server's limit. Checked here too so a large file is refused
// before it is read and sent.
const MAX_LOGO_BYTES = 256 * 1024;

const CompanyBranding = () => {
  const [companies, setCompanies] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [logos, setLogos] = useState({});
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newCompany, setNewCompany] = useState({ code: '', name: '', tb_customer_id: '' });
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [deviceCounts, setDeviceCounts] = useState({});
  const [grants, setGrants] = useState({});
  const [openGrants, setOpenGrants] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/branding/companies');
      setCompanies(res.data);
      const next = {};
      res.data.forEach((c) => {
        next[c.id] = { name: c.name, accent_color: c.accent_color || '' };
      });
      setDrafts(next);
    } catch (e) {
      setError('Could not load companies');
    }
  };

  useEffect(() => { load(); }, []);

  // Logos are behind the session, so they are fetched rather than linked.
  useEffect(() => {
    let cancelled = false;
    const created = [];

    companies.filter((c) => c.has_logo).forEach((c) => {
      api.get(`/branding/companies/${c.id}/logo`, { responseType: 'blob' })
        .then((res) => {
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          created.push(url);
          setLogos((prev) => ({ ...prev, [c.id]: url }));
        })
        .catch(() => {});
    });

    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [companies]);

  const openAddForm = async () => {
    const next = !adding;
    setAdding(next);
    if (!next) return;
    try {
      const res = await api.get('/branding/available-customers');
      setAvailableCustomers(res.data);
    } catch (e) {
      // Not fatal: without the list you can still create a fresh company.
      setAvailableCustomers([]);
    }
  };

  const addCompany = async (e) => {
    e.preventDefault();
    setBusy('new'); setError(null); setMessage(null);
    try {
      const res = await api.post('/branding/companies', newCompany);
      const p = res.data.provisioning;
      if (p && p.error) {
        // The company is saved either way; only the device-platform half failed.
        setError(`Company added, but its device-platform setup failed: ${p.error}. Use "Set up devices" to try again.`);
      } else {
        setMessage(`${res.data.company.name} added and set up on the device platform.`);
      }
      setNewCompany({ code: '', name: '' });
      setAdding(false);
      await load();
    } catch (e2) {
      setError(e2.response?.data?.error || 'Could not add the company');
    } finally {
      setBusy(null);
    }
  };

  const provision = async (id) => {
    setBusy(id); setError(null); setMessage(null);
    try {
      const res = await api.post(`/branding/companies/${id}/provision`);
      setMessage(res.data.steps.join('; '));
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not set that company up');
    } finally {
      setBusy(null);
    }
  };

  const syncDevices = async (id) => {
    setBusy(id); setError(null); setMessage(null);
    try {
      const res = await api.post(`/branding/companies/${id}/devices/sync`);
      const { total, added, removed } = res.data;
      setDeviceCounts((prev) => ({ ...prev, [id]: total }));
      setMessage(
        added || removed
          ? `${total} machine${total === 1 ? '' : 's'} (${added} added, ${removed} removed).`
          : `${total} machine${total === 1 ? '' : 's'}; nothing changed.`
      );
    } catch (e) {
      setError(e.response?.data?.error || 'Could not sync machines');
    } finally {
      setBusy(null);
    }
  };

  const loadGrants = async (id) => {
    if (openGrants === id) { setOpenGrants(null); return; }
    setBusy(id); setError(null);
    try {
      const res = await api.get(`/branding/companies/${id}/dashboards`);
      setGrants((prev) => ({ ...prev, [id]: res.data }));
      setOpenGrants(id);
    } catch (e) {
      setError('Could not load the dashboards');
    } finally {
      setBusy(null);
    }
  };

  const setGrant = async (companyId, dashboard, granted) => {
    setError(null); setMessage(null);
    try {
      await api.put(`/branding/companies/${companyId}/dashboards/${dashboard.id}`, { granted });
      setGrants((prev) => ({
        ...prev,
        [companyId]: prev[companyId].map((d) => (d.id === dashboard.id ? { ...d, granted } : d))
      }));
      setMessage(
        granted
          ? `${dashboard.name} is now available to that company.`
          : `${dashboard.name} is hidden from that company. Their users' setups are kept, so re-granting restores them.`
      );
    } catch (e) {
      setError(e.response?.data?.error || 'Could not change that grant');
    }
  };

  const setDraft = (id, field, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveDetails = async (id) => {
    setBusy(id); setError(null); setMessage(null);
    try {
      await api.put(`/branding/companies/${id}`, drafts[id]);
      setMessage('Saved. Anyone signed in to that company sees it on their next page load.');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save');
    } finally {
      setBusy(null);
    }
  };

  const uploadLogo = async (id, file) => {
    if (!file) return;
    setError(null); setMessage(null);

    if (file.size > MAX_LOGO_BYTES) {
      setError(`That image is ${Math.round(file.size / 1024)} KB. The limit is ${MAX_LOGO_BYTES / 1024} KB.`);
      return;
    }

    setBusy(id);
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = () => reject(new Error('Could not read that file'));
        reader.readAsDataURL(file);
      });

      await api.put(`/branding/companies/${id}/logo`, { data, mime: file.type });
      setMessage('Logo updated.');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not upload that image');
    } finally {
      setBusy(null);
    }
  };

  const removeLogo = async (id) => {
    setBusy(id); setError(null); setMessage(null);
    try {
      await api.delete(`/branding/companies/${id}/logo`);
      setLogos((prev) => ({ ...prev, [id]: undefined }));
      setMessage('Logo removed.');
      await load();
    } catch (e) {
      setError('Could not remove the logo');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="companies-section">
      <div className="section-header">
        <h2>Companies</h2>
        <button className="btn btn-primary" onClick={openAddForm}>
          <Plus size={18} /> Add company
        </button>
      </div>

      {adding && (
        <form className="company-card company-new" onSubmit={addCompany}>
          <div className="company-card-fields">
            <div className="form-group">
              <label htmlFor="new-company-code">Code</label>
              <input
                id="new-company-code"
                type="text"
                placeholder="LA"
                value={newCompany.code}
                onChange={(e) => setNewCompany({ ...newCompany, code: e.target.value })}
                required
              />
              <span className="form-help">
                The short key the machines already send. Letters, digits, hyphen or underscore.
              </span>
            </div>
            <div className="form-group">
              <label htmlFor="new-company-name">Name</label>
              <input
                id="new-company-name"
                type="text"
                placeholder="Linea Aqua"
                value={newCompany.name}
                onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                required
              />
              <span className="form-help">What their own people see after signing in.</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="new-company-customer">Device platform</label>
            <select
              id="new-company-customer"
              value={newCompany.tb_customer_id}
              onChange={(e) => setNewCompany({ ...newCompany, tb_customer_id: e.target.value })}
            >
              <option value="">Create a new one</option>
              {availableCustomers.map((cust) => (
                <option key={cust.id} value={cust.id} disabled={cust.claimed}>
                  {cust.title}{cust.claimed ? ' - already used by another company' : ''}
                </option>
              ))}
            </select>
            <span className="form-help">
              A company that was already on the device platform before this dashboard should use
              the entry it already has, so its machines and history come with it. Otherwise leave
              this alone and a new one is created.
            </span>
          </div>
          <div className="company-card-actions">
            <button className="btn btn-primary" type="submit" disabled={busy === 'new'}>
              {busy === 'new' ? 'Setting up...' : 'Add company'}
            </button>
            <button className="btn btn-secondary" type="button" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <p className="form-help">
        The name and logo a company&apos;s own people see once they sign in. The login page is
        shared, so it never shows any of this.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {companies.length === 0 && <p className="form-help">No companies yet.</p>}

      {companies.map((c) => (
        <div key={c.id} className="company-card">
          <div className="company-card-head">
            <div className="company-logo-slot">
              {logos[c.id]
                ? <img src={logos[c.id]} alt="" className="company-logo-preview" />
                : <span className="company-logo-empty">No logo</span>}
            </div>
            <div className="company-card-meta">
              <strong>{c.name}</strong>
              <span className="company-code">{c.code}</span>
              <span className="company-users">{c.user_count} user{Number(c.user_count) === 1 ? '' : 's'}</span>
              {!c.tb_user_id && (
                <span className="company-warn">No device-platform account yet</span>
              )}
              {deviceCounts[c.id] !== undefined && (
                <span className="company-users">{deviceCounts[c.id]} machine{deviceCounts[c.id] === 1 ? '' : 's'}</span>
              )}
            </div>
          </div>

          <div className="company-card-fields">
            <div className="form-group">
              <label htmlFor={`name-${c.id}`}>Display name</label>
              <input
                id={`name-${c.id}`}
                type="text"
                value={drafts[c.id]?.name ?? ''}
                onChange={(e) => setDraft(c.id, 'name', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor={`accent-${c.id}`}>Accent colour</label>
              <input
                id={`accent-${c.id}`}
                type="text"
                placeholder="#0d6570"
                value={drafts[c.id]?.accent_color ?? ''}
                onChange={(e) => setDraft(c.id, 'accent_color', e.target.value)}
              />
            </div>
          </div>

          {/* Read-only on purpose. The device mirror, the dashboard grants and
              every operator's machine bindings all hang off these two ids, so an
              edit box here would be an easy way to orphan a company's data.
              Shown because PrimeHive staff are the ones who have to diagnose it;
              a customer never sees this screen at all. */}
          <div className="company-binding">
            <span className="binding-label">Device platform</span>
            {c.tb_customer_id ? (
              <span className="binding-values">
                <span className="binding-pair">
                  <span className="binding-key">customer</span>
                  <code title={c.tb_customer_id}>{c.tb_customer_id}</code>
                </span>
                <span className="binding-pair">
                  <span className="binding-key">service account</span>
                  {c.tb_user_id
                    ? <code title={c.tb_user_id}>{c.tb_user_id}</code>
                    : <span className="company-warn">not created</span>}
                </span>
              </span>
            ) : (
              <span className="company-warn">not set up yet</span>
            )}
          </div>

          <div className="company-card-actions">
            <button
              className="btn btn-primary"
              disabled={busy === c.id}
              onClick={() => saveDetails(c.id)}
            >
              <Save size={16} /> Save
            </button>

            <label className="btn btn-secondary company-upload">
              <Upload size={16} /> {c.has_logo ? 'Replace logo' : 'Upload logo'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                hidden
                onChange={(e) => {
                  uploadLogo(c.id, e.target.files[0]);
                  e.target.value = '';
                }}
              />
            </label>

            {c.has_logo && (
              <button
                className="btn btn-danger"
                disabled={busy === c.id}
                onClick={() => removeLogo(c.id)}
              >
                <Trash2 size={16} /> Remove logo
              </button>
            )}

            {!c.tb_user_id && (
              <button
                className="btn btn-secondary"
                disabled={busy === c.id}
                onClick={() => provision(c.id)}
              >
                <Key size={16} /> Set up devices
              </button>
            )}

            {c.tb_customer_id && (
              <button
                className="btn btn-secondary"
                disabled={busy === c.id}
                onClick={() => syncDevices(c.id)}
              >
                <RefreshCw size={16} /> Sync machines
              </button>
            )}

            <button
              className="btn btn-secondary"
              disabled={busy === c.id}
              onClick={() => loadGrants(c.id)}
            >
              <LayoutDashboard size={16} /> Dashboards
            </button>
          </div>

          {openGrants === c.id && (
            <div className="company-grants">
              {(grants[c.id] || []).map((d) => (
                <label
                  key={d.id}
                  className={`grant-row ${d.is_available ? '' : 'grant-unavailable'}`}
                >
                  <input
                    type="checkbox"
                    checked={!!d.granted}
                    disabled={!d.is_available}
                    onChange={(e) => setGrant(c.id, d, e.target.checked)}
                  />
                  <span className="grant-name">{d.name}</span>
                  {!d.is_available && <span className="grant-note">not built yet</span>}
                </label>
              ))}
              {(grants[c.id] || []).length === 0 && (
                <p className="form-help">No dashboards in the catalogue.</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CompanyBranding;
